















































import Vue from "vue";
import {
  fetchTimezones,
  fetchUserTimezone,
  findTimezoneOption,
  updateUserTimezone,
  TimezoneOption,
} from "@/utils/timezone/timezone";
import { trackEvent } from "@/utils/analytics/analytics";
import {
  SETTINGS_TIMEZONE_SELECTED,
  SETTINGS_TIMEZONE_SAVED,
  SETTINGS_TIMEZONE_SAVE_FAILED,
} from "@/utils/analyticsEvents";

export default Vue.extend({
  name: "UserTimezone",
  data() {
    return {
      timezoneOptions: [] as TimezoneOption[],
      timezonesFailedToLoad: false,
      isLoading: true,
      // The zone currently stored server-side, and whether it was ever chosen by the member.
      effectiveTimezoneId: "",
      isUsingRegionalDefault: true,
      selectedTimezone: null as TimezoneOption | null,
      isSaving: false,
      saveError: "",
    };
  },
  async mounted(): Promise<void> {
    await this.loadTimezone();
  },
  computed: {
    componentText(): typeof this.getText.loggedIn.settings {
      return this.getText.loggedIn.settings;
    },
    hasUnsavedChanges(): boolean {
      return (
        !!this.selectedTimezone &&
        this.selectedTimezone.id !== this.effectiveTimezoneId
      );
    },
    statusText(): string {
      if (this.hasUnsavedChanges) return "";
      if (!this.isUsingRegionalDefault)
        return this.componentText.timezoneChosen;

      const effectiveTimezone = findTimezoneOption(
        this.timezoneOptions,
        this.effectiveTimezoneId,
      );
      return this.componentText.timezoneUsingDefault.replace(
        "{timezone}",
        effectiveTimezone ? effectiveTimezone.label : this.effectiveTimezoneId,
      );
    },
  },
  methods: {
    async loadTimezone(): Promise<void> {
      const userId = this.$store.getters.user_id;
      const [timezoneOptions, preference] = await Promise.all([
        fetchTimezones(),
        fetchUserTimezone(userId),
      ]);

      this.timezoneOptions = timezoneOptions;
      this.timezonesFailedToLoad = timezoneOptions.length === 0;

      // `timezone` is always the effective, valid zone; `is_default` tells us whether the
      // member ever chose it or inherited the regional fallback.
      const timezoneId =
        preference && preference.success && preference.timezone
          ? preference.timezone
          : "";
      this.effectiveTimezoneId = timezoneId;
      this.selectedTimezone = findTimezoneOption(timezoneOptions, timezoneId);
      this.isUsingRegionalDefault =
        !preference || preference.is_default !== false;
      this.isLoading = false;
      // Lets a parent hold one loader over the whole page instead of this section
      // spinning on its own. Emitted on failure too, or the page would never settle.
      this.$emit("ready");
    },
    onTimezoneChange(option: TimezoneOption | null): void {
      this.saveError = "";
      if (option) {
        trackEvent(SETTINGS_TIMEZONE_SELECTED, { value: option.id });
      }
    },
    async saveTimezone(): Promise<void> {
      if (!this.hasUnsavedChanges || this.isSaving || !this.selectedTimezone) {
        return;
      }
      this.isSaving = true;
      this.saveError = "";

      const wasDefault = this.isUsingRegionalDefault ? "Yes" : "No";
      const timezoneId = this.selectedTimezone.id;
      const response = await updateUserTimezone(
        this.$store.getters.user_id,
        timezoneId,
      );
      const succeeded = !!response && response.success === true;

      trackEvent(SETTINGS_TIMEZONE_SAVED, {
        value: timezoneId,
        success: succeeded ? "Yes" : "No",
        was_default: wasDefault,
      });

      if (succeeded) {
        this.effectiveTimezoneId = timezoneId;
        this.isUsingRegionalDefault = false;
        // Anything already reading the preference (the slot picker) should pick
        // the new zone up now, not on the next full load.
        this.$store.commit("setUserTimezone", timezoneId);
      } else {
        trackEvent(SETTINGS_TIMEZONE_SAVE_FAILED, {
          error_key: response ? "Rejected By Server" : "Request Failed",
        });
        // The backend bakes the reason into `err` — show it verbatim rather than re-deriving.
        this.saveError =
          (response && response.err) || this.componentText.timezoneSaveError;
      }
      this.isSaving = false;
    },
  },
});
